import { CantileverButton } from 'components/CantileverButton'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import {
  KNOWLEDGE_DOC_FILTER_CHANGE,
  KNOWLEDGE_DOC_OPTIONS_CHANGE,
  useGeckoAnalytics,
} from 'features/analytics'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import {
  FilterOption,
  TooltipOption,
  useKnowledgeDocumentUXContext,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { Settings } from 'lucide-react'

export function SettingsMenu() {
  const { trackEvent } = useGeckoAnalytics()
  const { tooltipOptions, setTooltipOptions, filterOptions, setFilterOptions } =
    useKnowledgeDocumentUXContext()
  const { totalPages } = useKnowledgeDocumentDataContext()

  const useTwoPageLayout = tooltipOptions.includes(
    TooltipOption.ShowMultiplePages
  )
  const autoOpenNewTab = tooltipOptions.includes(TooltipOption.AutoOpenNewTab)
  const showTooltips = tooltipOptions.includes(TooltipOption.ShowTooltips)
  const highlightClickableLinks = filterOptions !== FilterOption.RemoveBoxes
  const canUseTwoPageLayout = totalPages > 1

  const handleTwoPageLayoutChange = (checked: boolean) => {
    const currentlyShowingMultiplePages = tooltipOptions.includes(
      TooltipOption.ShowMultiplePages
    )

    if (checked === currentlyShowingMultiplePages) {
      return
    }

    let newOptions: TooltipOption[]
    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (checked) {
      newOptions = [...tooltipOptions]
      if (!newOptions.includes(TooltipOption.ShowMultiplePages)) {
        newOptions.push(TooltipOption.ShowMultiplePages)
        added.push(TooltipOption.ShowMultiplePages)
      }
      if (!newOptions.includes(TooltipOption.MultiPageTurner)) {
        newOptions.push(TooltipOption.MultiPageTurner)
        added.push(TooltipOption.MultiPageTurner)
      }
    } else {
      newOptions = tooltipOptions.filter(
        (opt) =>
          opt !== TooltipOption.ShowMultiplePages &&
          opt !== TooltipOption.MultiPageTurner
      )
      if (tooltipOptions.includes(TooltipOption.ShowMultiplePages)) {
        removed.push(TooltipOption.ShowMultiplePages)
      }
      if (tooltipOptions.includes(TooltipOption.MultiPageTurner)) {
        removed.push(TooltipOption.MultiPageTurner)
      }
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: newOptions,
      added_options: added,
      removed_options: removed,
    })
    setTooltipOptions(newOptions)
  }

  const handleAutoOpenNewTabChange = (checked: boolean) => {
    const currentlyEnabled = tooltipOptions.includes(
      TooltipOption.AutoOpenNewTab
    )

    if (checked === currentlyEnabled) {
      return
    }

    let newOptions: TooltipOption[]
    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (checked) {
      newOptions = [...tooltipOptions, TooltipOption.AutoOpenNewTab]
      added.push(TooltipOption.AutoOpenNewTab)
    } else {
      newOptions = tooltipOptions.filter(
        (opt) => opt !== TooltipOption.AutoOpenNewTab
      )
      removed.push(TooltipOption.AutoOpenNewTab)
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: newOptions,
      added_options: added,
      removed_options: removed,
    })
    setTooltipOptions(newOptions)
  }

  const handleShowTooltipsChange = (checked: boolean) => {
    const currentlyEnabled = tooltipOptions.includes(TooltipOption.ShowTooltips)

    if (checked === currentlyEnabled) {
      return
    }

    let newOptions: TooltipOption[]
    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (checked) {
      newOptions = [...tooltipOptions, TooltipOption.ShowTooltips]
      added.push(TooltipOption.ShowTooltips)
    } else {
      newOptions = tooltipOptions.filter(
        (opt) => opt !== TooltipOption.ShowTooltips
      )
      removed.push(TooltipOption.ShowTooltips)
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: newOptions,
      added_options: added,
      removed_options: removed,
    })
    setTooltipOptions(newOptions)
  }

  const handleHighlightClickableLinksChange = (checked: boolean) => {
    const currentlyShowingBoxes = filterOptions !== FilterOption.RemoveBoxes

    if (checked === currentlyShowingBoxes) {
      return
    }

    const newFilter = checked
      ? FilterOption.ShowConfirmed
      : FilterOption.RemoveBoxes

    trackEvent(KNOWLEDGE_DOC_FILTER_CHANGE.title, {
      filter_option: newFilter,
      previous_filter: filterOptions,
    })
    setFilterOptions(newFilter)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <CantileverButton iconOnly title='Settings'>
          <Settings size={18} strokeWidth={1.5} />
        </CantileverButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start'>
        <DropdownMenuCheckboxItem
          checked={highlightClickableLinks}
          onCheckedChange={handleHighlightClickableLinksChange}>
          Highlight clickable links
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={showTooltips}
          onCheckedChange={handleShowTooltipsChange}>
          Show tooltip when hovering on links
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={autoOpenNewTab}
          onCheckedChange={handleAutoOpenNewTabChange}>
          Always open links in new tab
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={useTwoPageLayout}
          onCheckedChange={handleTwoPageLayoutChange}
          disabled={!canUseTwoPageLayout}>
          Use two-page layout
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
